import { forwardRef } from 'react';
import { Box, Typography } from '@mui/material';
import type { SxProps, Theme, TypographyProps } from '@mui/material';
import { splitEdgeWhitespace } from '@/shared/utils';

interface WhitespaceHighlightedTextProps extends Omit<TypographyProps, 'children'> {
  text: string;
}

const edgeMarkerSx: SxProps<Theme> = (theme) => ({
  whiteSpace: 'pre',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 193, 7, 0.28)' : 'rgba(255, 193, 7, 0.35)',
  borderRadius: 0.5,
  '&::before': {
    content: '"·"',
    color: theme.palette.text.disabled,
  },
});

/**
 * Renders text with leading/trailing whitespace visibly marked (background +
 * a dot decoration), so a value that must ship with a trailing space (Figma
 * requirement) is verifiably complete at a glance.
 *
 * Requires `white-space: pre-wrap` (applied by default here, forwardable via
 * `sx`) — otherwise the browser collapses the edge whitespace before this
 * component ever sees it rendered, and there is nothing left to highlight.
 */
export const WhitespaceHighlightedText = forwardRef<HTMLElement, WhitespaceHighlightedTextProps>(
  function WhitespaceHighlightedText({ text, sx, ...typographyProps }, ref) {
    const { leading, core, trailing } = splitEdgeWhitespace(text);

    return (
      <Typography
        ref={ref}
        sx={[{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }, ...(Array.isArray(sx) ? sx : [sx])]}
        {...typographyProps}
      >
        {leading && (
          <Box component="span" aria-hidden="true" sx={edgeMarkerSx}>
            {leading}
          </Box>
        )}
        {core}
        {trailing && (
          <Box component="span" aria-hidden="true" sx={edgeMarkerSx}>
            {trailing}
          </Box>
        )}
      </Typography>
    );
  }
);
